<template>
  <v-row>
    <v-col cols="12" class="px-0">
      <v-card flat class="customer-form-card">
        <v-card-text class="px-0 pt-0">
          <!-- Header -->
          <div class="form-hero">
            <div class="form-hero__left">
              <div class="form-hero__avatar" aria-hidden="true">
                {{ initials(form.first_name) }}
              </div>
              <div class="form-hero__meta">
                <div class="form-hero__title">
                  {{ $t(form.id ? "update" : "new_customer") }}
                </div>
                <div class="form-hero__subtitle">
                  {{ $t("contact_information") }} · {{ $t("location_details") }}
                </div>
              </div>
            </div>
            <div class="form-hero__right">
              <v-chip v-if="form.id" small outlined class="form-hero__chip">
                ID #{{ form.id }}
              </v-chip>
            </div>
          </div>

          <v-form @submit.prevent="saveCustomer" class="customer-form">
            <!-- Identity + Contact -->
            <div class="section">
              <div class="section-header">
                <div class="section-icon">
                  <v-icon small>fal fa-user</v-icon>
                </div>
                <div>
                  <div class="section-title">{{ $t("personal_information") }}</div>
                  <div class="section-desc">{{ $t("contact_information") }}</div>
                </div>
              </div>

              <v-row class="mt-3">
                <v-col cols="12" md="6">
                  <TextField v-model="form.first_name" label="full_name" />
                </v-col>
                <v-col cols="12" md="6">
                  <TextField v-model="form.last_name" label="last_name" />
                </v-col>
                <v-col cols="12" md="6">
                  <TextField
                    v-model="form.phone_no"
                    label="phone_no"
                    :maxlength="10"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <TextField v-model="form.email" label="email" />
                </v-col>
              </v-row>
            </div>

            <!-- Logistics -->
            <div class="section mt-6">
              <div class="section-header">
                <div class="section-icon">
                  <v-icon small>fal fa-truck</v-icon>
                </div>
                <div>
                  <div class="section-title">{{ $t("logistics_partner") }}</div>
                </div>
              </div>

              <v-row class="mt-3">
                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-for="item in logistics"
                  :key="item.id"
                >
                  <v-card
                    class="logistic-option"
                    :class="{ selected: form.logistics_id === item.id }"
                    elevation="0"
                    outlined
                    role="button"
                    tabindex="0"
                    :aria-pressed="String(form.logistics_id === item.id)"
                    @click="form.logistics_id = item.id"
                    @keydown.enter.prevent="form.logistics_id = item.id"
                    @keydown.space.prevent="form.logistics_id = item.id"
                  >
                    <div class="logistic-content">
                      <v-avatar size="52" class="mb-2">
                        <v-img :src="item.logo" :alt="item.name" />
                      </v-avatar>
                      <div class="logistic-title">{{ item.name }}</div>
                      <div class="check" v-if="form.logistics_id === item.id">
                        <v-icon x-small color="white">fa-solid fa-check</v-icon>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Location -->
            <div class="section mt-6">
              <div class="section-header">
                <div class="section-icon">
                  <v-icon small>fal fa-map-marker-alt</v-icon>
                </div>
                <div>
                  <div class="section-title">{{ $t("location_details") }}</div>
                  <div class="section-desc">{{ $t("location_information") }}</div>
                </div>
              </div>
              <v-row class="mt-3">
                <v-col cols="12" md="6">
                  <TextSelect
                    v-model="form.province_id"
                    :items="provinces"
                    itemValue="pr_id"
                    text="pr_name"
                    label="province"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <TextSelect
                    v-model="form.district_id"
                    :items="districts"
                    itemValue="dr_id"
                    text="dr_name"
                    label="district"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <TextSelect
                    v-model="form.village_id"
                    :items="villages"
                    itemValue="vill_id"
                    text="vill_name"
                    label="village"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- Address -->
            <div class="section mt-6">
              <div class="section-header">
                <div class="section-icon">
                  <v-icon small>fal fa-home</v-icon>
                </div>
                <div>
                  <div class="section-title">{{ $t("detailed_address") }}</div>
                </div>
              </div>
              <v-row class="mt-3">
                <v-col cols="12">
                  <app-text-area v-model="form.address" label="address" />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import TextField from "@/components/global/TextField";
import TextSelect from "@/components/global/TextSelect";
import { mapGetters, mapActions } from "vuex";
import { toast } from "@/controllers/alert";
import AppTextArea from "@/components/global/TextArea";

export default {
  components: {
    TextField,
    TextSelect,
    AppTextArea,
  },

  props: {
    customerData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      tab: "customer",
      tabs: [
        {
          name: "customer",
          icon: "fal fa-users",
        },
        {
          name: "add_customer",
          icon: "fal fa-user-plus",
        },
      ],
      search: "",
      selectedCustomerId: null,
      form: {
        first_name: "",
        last_name: "",
        phone_no: "20",
        email: "",
        address: "",
        province_id: "",
        district_id: "",
        village_id: "",
        logistics_id: "",
      },
      prefillDefaults: null,
    };
  },
  mounted() {
    if (this.customer_id) {
      this.selectedCustomerId = this.customer_id;
    }
    this.fetchLogistics(this);
    this.fetchProvinces(this);
    this.tab = "add_customer";
  },

  watch: {
    customerData: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.prefillForm(newVal);
        } else {
          this.clearForm();
        }
      },
      immediate: true,
    },
    provinces(val) {
      if (
        this.prefillDefaults?.pr_id &&
        Array.isArray(val) &&
        val.length &&
        !this.form.province_id
      ) {
        const found = val.find((p) => p.pr_id === this.prefillDefaults.pr_id);
        if (found) {
          this.form.province_id = found;
        }
      }
    },
    districts(val) {
      if (
        this.prefillDefaults?.dr_id &&
        Array.isArray(val) &&
        val.length &&
        !this.form.district_id
      ) {
        const found = val.find((d) => d.dr_id === this.prefillDefaults.dr_id);
        if (found) {
          this.form.district_id = found;
        }
      }
    },
    villages(val) {
      if (
        this.prefillDefaults?.vill_id &&
        Array.isArray(val) &&
        val.length &&
        !this.form.village_id
      ) {
        const found = val.find(
          (v) => v.vill_id === this.prefillDefaults.vill_id
        );
        if (found) {
          this.form.village_id = found;
          this.prefillDefaults = null;
        }
      }
    },
    "form.province_id": function (val) {
      if (val) {
        const params = {
          that: this,
          pr_id: val.pr_id,
        };
        this.fetchDistricts(params);
      }
    },
    "form.district_id": function (val) {
      if (val) {
        const params = {
          that: this,
          dr_id: val.dr_id,
        };
        this.fetchVillages(params);
      }
    },
  },

  computed: {
    ...mapGetters({
      customers: "index/customers",
      logistics: "index/logistics",
      provinces: "index/provinces",
      districts: "index/districts",
      villages: "index/villages",
    }),

    filteredCustomers() {
      const q = (this.search || "").toLowerCase();
      if (!q) return this.customers;
      return this.customers.filter(
        (c) =>
          c.first_name.toLowerCase().includes(q) ||
          (c.phone_no || "").toLowerCase().includes(q)
      );
    },
  },
  methods: {
    ...mapActions({
      fetchLogistics: "index/fetchLogistics",
      fetchProvinces: "index/fetchProvinces",
      fetchDistricts: "index/fetchDistricts",
      fetchVillages: "index/fetchVillages",
      fetch: "index/fetch",
    }),

    prefillForm(cus) {
      this.form = {
        id: cus.id,
        first_name: cus.first_name || "",
        last_name: cus.last_name || "",
        phone_no: cus.phone_no || "20",
        email: cus.email || "",
        address: cus.address || "",
        province_id: "",
        district_id: "",
        village_id: "",
        logistics_id: cus.logistics_id || (cus.logistics?.id ?? ""),
      };

      // Remember defaults to auto-select as lists load
      this.prefillDefaults = {
        pr_id: cus?.village?.district?.province?.pr_id,
        dr_id: cus?.village?.district?.dr_id,
        vill_id: cus?.village?.vill_id || cus?.village_id,
      };

      // If we already have provinces loaded, attempt to select immediately
      if (this.provinces?.length && this.prefillDefaults?.pr_id) {
        const p = this.provinces.find(
          (x) => x.pr_id === this.prefillDefaults.pr_id
        );
        if (p) this.form.province_id = p;
      }

      // When province watcher fires, it will load districts, then villages
    },
    async saveCustomer() {
      try {
        const body = {
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          phone_no: this.form.phone_no,
          email: this.form.email,
          address: this.form.address,
          village_id:
            this.form.village_id?.vill_id || this.form.village_id || null,
          logistics_id:
            (typeof this.form.logistics_id === "object"
              ? this.form.logistics_id?.id
              : this.form.logistics_id) || null,
        };
        let resp;
        if (this.form.id) {
          resp = await this.$http.post(
            `customers_update/${this.form.id}`,
            body
          );
        } else {
          resp = await this.$http.post("customers_add", body);
        }
        const { data } = resp;
        if (data.status) {
          await toast(this, data.message, "success");
          this.$emit("saved", data.data || body);
          this.clearForm();
        }
      } catch (error) {
        console.log(error);
      }
    },
    submit() {
      this.saveCustomer();
    },
    clearForm() {
      this.form = {
        first_name: "",
        last_name: "",
        phone_no: "20",
        email: "",
        address: "",
        province_id: "",
        district_id: "",
        village_id: "",
        logistics_id: "",
      };
    },
    back() {
      this.$router.back();
    },
    initials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/);
      const first = parts[0]?.[0] || "?";
      const last = (parts[1] && parts[1][0]) || "";
      return (first + last).toUpperCase();
    },
    selectCustomer(id) {
      this.selectedCustomerId = id;
      this.$emit("selectCustomer", id);
    },
  },
};
</script>

<style scoped>
.customer-form-card {
  border-radius: 16px;
}

/* Scoped tokens (fallbacks) */
.customer-form-card {
  --c-primary: var(--color-primary, #5b39c9);
  --c-primary-soft: var(--color-primary-10, rgba(91, 57, 201, 0.1));
  --c-primary-ring: var(--color-primary-20, rgba(91, 57, 201, 0.2));
  --c-text: var(--text-primary, #0f172a);
  --c-muted: var(--text-muted, #64748b);
  --c-border: rgba(15, 23, 42, 0.08);
  --c-border-strong: rgba(15, 23, 42, 0.12);
  --c-surface: var(--bg-primary, #ffffff);
  --c-surface-2: #f8fafc;
}

.form-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 18px 14px;
  border-bottom: 1px solid var(--c-border);
  background: var(--c-surface-2);
  border-radius: 16px 16px 0 0;
}

.form-hero__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.form-hero__avatar {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: var(--c-primary);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  letter-spacing: 0.02em;
  box-shadow: 0 10px 24px var(--c-primary-ring);
  flex-shrink: 0;
}

.form-hero__meta {
  min-width: 0;
}

.form-hero__title {
  font-size: 16px;
  font-weight: 800;
  color: var(--c-text);
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.form-hero__subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: var(--c-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.form-hero__chip {
  border-color: var(--c-border-strong) !important;
  color: var(--c-muted) !important;
  font-weight: 600;
}

.customer-form {
  padding: 16px 0 0;
}

.section {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 14px;
  padding: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--c-primary-soft);
  border: 1px solid var(--c-primary-ring);
  color: var(--c-primary);
  flex-shrink: 0;
}

.section-title {
  font-weight: 800;
  color: var(--c-text);
  line-height: 1.2;
}

.section-desc {
  margin-top: 2px;
  font-size: 12px;
  color: var(--c-muted);
}

.logistic-option {
  position: relative;
  border-radius: 16px;
  border: 1px solid var(--c-border);
  background: #fff;
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 200ms ease, background-color 200ms ease;
  cursor: pointer;
}

.logistic-option:hover {
  transform: none;
  box-shadow: none;
  border-color: var(--c-border);
}

.logistic-option:focus-visible {
  outline: 3px solid var(--c-primary-ring);
  outline-offset: 2px;
}

.logistic-option.selected {
  border-color: var(--c-border) !important;
  background: #fff !important;
}

.logistic-content {
  text-align: center;
}

.logistic-title {
  margin-top: 6px;
  font-weight: 800;
  color: var(--c-text);
  font-size: 13px;
}
.check {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--c-primary);
}
</style>
